import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Users, Package, TrendingUp, RotateCcw, Clock } from 'lucide-react';

const S = {
  page: { color: '#f5f0e8' },
  heading: {
    fontSize: 26,
    fontWeight: 300,
    letterSpacing: '0.06em',
    color: '#f5f0e8',
    marginBottom: 4,
  },
  sub: {
    fontSize: 13,
    color: 'rgba(245,240,232,0.35)',
    marginBottom: 32,
    letterSpacing: '0.03em',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 16,
    marginBottom: 32,
  },
  card: (accent) => ({
    background: '#13110d',
    border: `1px solid rgba(${accent},0.15)`,
    borderRadius: 10,
    padding: '20px 22px',
    position: 'relative',
    overflow: 'hidden',
  }),
  cardAccent: (accent) => ({
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 2,
    background: `rgba(${accent},0.6)`,
  }),
  cardLabel: {
    fontSize: 10,
    letterSpacing: '0.4em',
    color: 'rgba(245,240,232,0.35)',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  cardValue: {
    fontSize: 30,
    fontWeight: 300,
    color: '#f5f0e8',
    letterSpacing: '0.04em',
  },
  cardSub: {
    fontSize: 12,
    color: 'rgba(245,240,232,0.3)',
    marginTop: 4,
  },
  section: {
    background: '#13110d',
    border: '1px solid rgba(186,130,69,0.1)',
    borderRadius: 10,
    overflow: 'hidden',
    marginBottom: 24,
  },
  sectionHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid rgba(186,130,69,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontSize: 13,
    letterSpacing: '0.08em',
    color: '#f5f0e8',
    fontWeight: 400,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    fontSize: 9,
    letterSpacing: '0.35em',
    color: 'rgba(245,240,232,0.25)',
    textTransform: 'uppercase',
    padding: '10px 20px',
    textAlign: 'left',
    borderBottom: '1px solid rgba(186,130,69,0.06)',
  },
  td: {
    padding: '14px 20px',
    borderBottom: '1px solid rgba(186,130,69,0.05)',
    fontSize: 14,
    color: '#f5f0e8',
    verticalAlign: 'middle',
  },
};

const statusColors = {
  pending: { bg: 'rgba(234,179,8,0.12)', text: '#fde047', border: 'rgba(234,179,8,0.3)' },
  processing: { bg: 'rgba(59,130,246,0.12)', text: '#93c5fd', border: 'rgba(59,130,246,0.3)' },
  shipped: { bg: 'rgba(139,92,246,0.12)', text: '#c4b5fd', border: 'rgba(139,92,246,0.3)' },
  paid: { bg: 'rgba(34,197,94,0.12)', text: '#86efac', border: 'rgba(34,197,94,0.3)' },
  delivered: { bg: 'rgba(34,197,94,0.12)', text: '#86efac', border: 'rgba(34,197,94,0.3)' },
  cancelled: { bg: 'rgba(239,68,68,0.12)', text: '#fca5a5', border: 'rgba(239,68,68,0.3)' },
};

const StatusBadge = ({ status }) => {
  const c = statusColors[status] || statusColors.pending;
  return (
    <span style={{
      background: c.bg, color: c.text,
      border: `1px solid ${c.border}`,
      borderRadius: 20, padding: '3px 10px',
      fontSize: 11, letterSpacing: '0.06em',
      textTransform: 'uppercase',
    }}>
      {status}
    </span>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState({ orders: 0, revenue: 0, customers: 0, products: 0, returns: 0, pendingOrders: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      const [ordersRes, productsRes, returnsRes, customersRes] = await Promise.all([
        supabase.from('orders').select('id, status, total_amount, created_at, email').order('created_at', { ascending: false }),
        supabase.from('products').select('id, is_live'),
        supabase.from('returns').select('id'),
        supabase.from('orders').select('email'),
      ]);

      const orders = ordersRes.data || [];
      const paidOrders = orders.filter(o => ['paid', 'processing', 'shipped', 'delivered'].includes(o.status));
      const revenue = paidOrders.reduce((sum, o) => sum + (parseFloat(o.total_amount) || 0), 0);
      const uniqueEmails = new Set((customersRes.data || []).map(o => o.email).filter(Boolean));

      setStats({
        orders: orders.length,
        revenue,
        customers: uniqueEmails.size,
        products: (productsRes.data || []).length,
        returns: (returnsRes.data || []).length,
        pendingOrders: orders.filter(o => o.status === 'pending').length,
      });
      setRecentOrders(orders.slice(0, 8));
      setLoading(false);
    };
    load();
  }, []);

  const statCards = [
    { label: 'Total Revenue', value: `₹${stats.revenue.toLocaleString('en-IN')}`, accent: '186,130,69', sub: 'All paid orders', icon: TrendingUp },
    { label: 'Total Orders', value: stats.orders, accent: '59,130,246', sub: `${stats.pendingOrders} pending`, icon: ShoppingBag },
    { label: 'Customers', value: stats.customers, accent: '139,92,246', sub: 'Unique buyers', icon: Users },
    { label: 'Products', value: stats.products, accent: '34,197,94', sub: 'In catalogue', icon: Package },
    { label: 'Returns', value: stats.returns, accent: '239,68,68', sub: 'All time', icon: RotateCcw },
    { label: 'Pending', value: stats.pendingOrders, accent: '234,179,8', sub: 'Awaiting action', icon: Clock },
  ];

  return (
    <div style={S.page}>
      <div style={S.heading}>Dashboard</div>
      <div style={S.sub}>Welcome back, Manisha · {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</div>

      {/* Stats Grid */}
      <div style={S.grid}>
        {statCards.map(card => {
          const Icon = card.icon;
          return (
            <div key={card.label} style={S.card(card.accent)}>
              <div style={S.cardAccent(card.accent)} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={S.cardLabel}>{card.label}</div>
                <Icon size={14} style={{ color: `rgba(${card.accent},0.6)`, marginTop: 2 }} />
              </div>
              <div style={S.cardValue}>{loading ? '—' : card.value}</div>
              <div style={S.cardSub}>{card.sub}</div>
            </div>
          );
        })}
      </div>

      {/* Recent Orders */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <div style={S.sectionTitle}>Recent Orders</div>
          <button
            onClick={() => navigate('/admin/orders')}
            style={{
              background: 'none', border: '1px solid rgba(186,130,69,0.2)',
              borderRadius: 5, padding: '5px 12px', color: '#ba8245',
              fontSize: 11, letterSpacing: '0.15em', cursor: 'pointer',
              fontFamily: "'Cormorant Garamond', Georgia, serif",
            }}
          >
            View All
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Order ID', 'Customer', 'Amount', 'Status', 'Date'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentOrders.map(order => (
                <tr
                  key={order.id}
                  onClick={() => navigate('/admin/orders')}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(186,130,69,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ ...S.td, color: '#ba8245', fontWeight: 500 }}>#{order.id}</td>
                  <td style={{ ...S.td, color: 'rgba(245,240,232,0.7)' }}>{order.email || 'Guest'}</td>
                  <td style={S.td}>₹{parseFloat(order.total_amount || 0).toLocaleString('en-IN')}</td>
                  <td style={S.td}><StatusBadge status={order.status} /></td>
                  <td style={{ ...S.td, color: 'rgba(245,240,232,0.4)', fontSize: 12 }}>
                    {new Date(order.created_at).toLocaleDateString('en-IN')}
                  </td>
                </tr>
              ))}
              {!loading && recentOrders.length === 0 && (
                <tr><td colSpan={5} style={{ ...S.td, textAlign: 'center', color: 'rgba(245,240,232,0.25)' }}>No orders yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
